import { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import * as S from './Tooltip.styles.tsx';
import P from '../texts/p/P.tsx';

const getPortalPosition = (rect: DOMRect, position: string) => {
  switch (position) {
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: rect.right,
        transform: 'translateY(-50%)',
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: rect.left,
        transform: 'translateY(-50%) translateX(-100%)',
      };
    case 'bottom':
    case 'bottom-left':
    case 'bottom-right':
      return {
        top: rect.bottom,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      };
    case 'top':
    case 'top-left':
    case 'top-right':
    default:
      return {
        top: rect.top,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%) translateY(-100%)',
      };
  }
};

const Tooltip = ({ position, showArrow, children, msg, show, align }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, transform: '' });
  const triggerRef = useRef<HTMLDivElement>(null);

  const alignText = {
    top: 'center',
    right: 'left',
    bottom: 'center',
    left: 'right',
    'top-right': 'left',
    'top-left': 'right',
    'bottom-right': 'left',
    'bottom-left': 'right',
  };

  const isVisible = show !== undefined ? show : isHovered;

  const handleMouseEnter = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords(getPortalPosition(rect, position));
    }
    setIsHovered(true);
  }, [position]);

  const overlay = isVisible
    ? ReactDOM.createPortal(
        <S.TooltipOverlay
          style={{
            position: 'fixed',
            zIndex: 9999,
            top: coords.top,
            left: coords.left,
            transform: coords.transform,
          }}
        >
          <S.TooltipWrapper data-testid='tooltip' position={position}>
            <S.TooltipMessage>
              <P
                type='4'
                variant='neutral'
                modifier='inverted'
                align={align ? align : alignText[position]}
              >
                {msg}
              </P>
            </S.TooltipMessage>
            {showArrow && (
              <S.TooltipArrow data-testid='tooltip-arrow' position={position} />
            )}
          </S.TooltipWrapper>
        </S.TooltipOverlay>,
        document.body
      )
    : null;

  return (
    <S.Tooltip position={position} msg={msg} showArrow={showArrow} show={isVisible}>
      <S.ChildrenWrapper
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={handleMouseEnter}
        onBlur={() => setIsHovered(false)}
      >
        {children}
      </S.ChildrenWrapper>
      {overlay}
    </S.Tooltip>
  );
};

Tooltip.defaultProps = {
  position: 'top',
  size: 'md',
  showArrow: true,
};

Tooltip.propTypes = {
  position: PropTypes.oneOf([
    'top',
    'right',
    'bottom',
    'left',
    'top-right',
    'top-left',
    'bottom-right',
    'bottom-left',
  ]),
  showArrow: PropTypes.bool,
  show: PropTypes.bool,
  msg: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  align: PropTypes.oneOf(['center', 'right', 'left']),
};

export default Tooltip;
